/// Remote knobs from `GET /v1/config` (Postgres `app_config` row).

import { supportedLocales, type SupportedLocale } from '@/app/i18n/resources';

export interface RemoteAppConfig {
  isMaintenance: boolean;
  /** Legacy English field — still returned for older web builds. */
  maintenanceMessage: string;
  maintenanceMessages: Partial<Record<SupportedLocale, string>>;
  /** Legacy single floor (older clients). Prefer platform fields. */
  minimumVersion: string;
  minimumVersionIos: string;
  minimumVersionAndroid: string;
  minimumVersionWeb: string;
  latestVersion: string;
}

function isSupportedLocale(value: string): value is SupportedLocale {
  return (supportedLocales as readonly string[]).includes(value);
}

function parseMessages(raw: unknown): Partial<Record<SupportedLocale, string>> {
  if (typeof raw !== 'object' || raw === null) return {};
  const out: Partial<Record<SupportedLocale, string>> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== 'string' || value.trim().length === 0) continue;
    const locale = key === 'zh' ? 'zh-CN' : key;
    if (!isSupportedLocale(locale)) continue;
    out[locale] = value;
  }
  return out;
}

export function remoteAppConfigFromJson(json: Record<string, unknown>): RemoteAppConfig {
  const legacy =
    typeof json.minimum_version === 'string' ? json.minimum_version : '0.0.1';
  return {
    isMaintenance: json.is_maintenance === true,
    maintenanceMessage:
      typeof json.maintenance_message === 'string'
        ? json.maintenance_message
        : 'System maintenance in progress',
    maintenanceMessages: parseMessages(json.maintenance_messages),
    minimumVersion: legacy,
    minimumVersionIos:
      typeof json.minimum_version_ios === 'string' ? json.minimum_version_ios : legacy,
    minimumVersionAndroid:
      typeof json.minimum_version_android === 'string'
        ? json.minimum_version_android
        : legacy,
    minimumVersionWeb:
      typeof json.minimum_version_web === 'string' ? json.minimum_version_web : legacy,
    latestVersion: typeof json.latest_version === 'string' ? json.latest_version : '1.0.0',
  };
}

/** Force-update floor for this web build. */
export function minimumVersionForWeb(config: RemoteAppConfig): string {
  return config.minimumVersionWeb || config.minimumVersion;
}

export function maintenanceMessageFor(
  config: RemoteAppConfig,
  locale: SupportedLocale,
  l10nFallback: string,
): string {
  const localized = config.maintenanceMessages[locale]?.trim();
  if (localized) return localized;

  const english = config.maintenanceMessages.en?.trim();
  if (english) return english;

  const legacy = config.maintenanceMessage.trim();
  if (legacy) return legacy;

  return l10nFallback;
}

/** Simple `major.minor.patch` compare. Returns negative if `a` < `b`. */
export function compareSemver(a: string, b: string): number {
  const parts = (value: string): [number, number, number] => {
    const raw = value.split('.');
    return [
      Number.parseInt(raw[0] ?? '0', 10) || 0,
      Number.parseInt(raw[1] ?? '0', 10) || 0,
      Number.parseInt(raw[2] ?? '0', 10) || 0,
    ];
  };

  const left = parts(a);
  const right = parts(b);
  for (let i = 0; i < 3; i += 1) {
    const cmp = left[i]! - right[i]!;
    if (cmp !== 0) return cmp;
  }
  return 0;
}
